import CustomerOrdersApiV2 from 'api/v2/admins/customer-orders-api-v2'
import CardLoader from 'components/organisms/card-loader'
import StringUtils from 'utils/string-util'
import {
  CustomerOrderDetailCard,
  CustomerOrderShippingAddressCard
} from 'domains/customer-order/organisms'
import CustomerDetailCard from 'domains/customer/organisms/detail-card'
import ItemPage from './customer-order-detail-item-page'
import PaymentPage from './customer-order-detail-payment-page'
import ShipmentPage from './customer-order-detail-shipment-page'
import withLoader from 'hocs/with-loader'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import {
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Alert
} from 'reactstrap'
import { TitlePage } from 'components/atoms'
import { AFFILIATE_NAMES, STATUS } from 'domains/customer-order/constants'
import { useModal } from 'providers/modal-provider'
import { ALERT_TYPES } from 'constants/alert-constants'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import AdminDetailCard from 'domains/admin/detail-card/admin-detail-card'
import GenericModal from 'components/organisms/generic-modal'
import SalesAdminForm from 'domains/customer-order/organisms/sales-admin-form'
import { useAuth } from 'providers/auth-provider'
import { ACCESS } from 'domains/admin/constants'

const TAB = {
  ITEMS     : 'items',
  SHIPMENTS : 'shipments',
  PAYMENTS  : 'payments'
}

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const CustomerOrderDetailPage = ({ pageUtils }) => {
  const { id }                            = useParams()
  const location                          = useLocation()
  const [ refetchOrder, setRefetchOrder ] = useState(false)
  const [ isModalOpen, setIsModalOpen ]   = useState(false)

  useEffect(() => {
    const tab = location.hash.replace('#', '')
    if (tab) {
      setActiveTab(tab)
    } else {
      setActiveTab(TAB.ITEMS)
    }
  }, [ location.hash ])

  const [ activeTab, setActiveTab ] = useState('')
  const [ order, setOrder ]         = useState({})
  const [ isLoading, setIsLoading ] = useState(true)

  const { user }              = useAuth()
  const isAdminSalesLead      = user.role === ACCESS.SALES_LEAD
  const showZaloraOrderButton = order.affiliate_name === AFFILIATE_NAMES.ZALORA

  const toggleSalesAdminModal = () => setIsModalOpen(!isModalOpen)

  /** Handler to fetch Customer Order */
  const handleFetchCustomerOrder = useCallback(() => {
    setIsLoading(true)
    CustomerOrdersApiV2.show(id)
      .then((response) => {
        setOrder(response.data.order)
        setIsLoading(false)
        setRefetchOrder(false)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ id, pageUtils ])


  useEffect(() => {
    handleFetchCustomerOrder()
  }, [ handleFetchCustomerOrder ])

  useEffect(() => {
    if (refetchOrder) handleFetchCustomerOrder()
  }, [ refetchOrder ])

  const { showModal, hideModal } = useModal()

  const handleClickCancel = () => {
    showModal(ConfirmationModal, {
      title              : 'Cancel Confirmation',
      children           : `Apakah kamu yakin akan cancel order ${order.number} ?`,
      handleConfirmation : async () => handleCancelOrder()
    })
  }

  // handler to cancel order
  const handleCancelOrder = () => {
    CustomerOrdersApiV2.cancel(order.id)
      .then(() => {
        pageUtils.setAlertMsg('Order successfully cancelled', ALERT_TYPES.SUCCESS)
        handleFetchCustomerOrder()
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => hideModal())
  }

  const CustomerOrderDetailCardWithLoader = withLoader(CustomerOrderDetailCard, CardLoader)

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const handleSubmitAssignSalesAdmin = (values, formikBag) => {
    let params = {
      order_id : order.id,
      admin_id : values.admin.id
    }
    formikBag.setSubmitting(true)
    CustomerOrdersApiV2.assignSalesAdmin(params)
      .then((response) => {
        setIsModalOpen(false)
        formikBag.resetForm()
        pageUtils.setAlertMsg(
          'Berhasil assign sales admin ke customer order!',
          ALERT_TYPES.SUCCESS
        )
        setOrder(response.data.order)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => formikBag.setSubmitting(false))
  }

  return (
    <>
      <TitlePage mainTitle={ 'Order' } subTitle={ `No. ${order.number}` } />

      <Nav pills className={ 'mb-3' }>
        <NavItem>
          <NavLink
            className={ activeTab === TAB.ITEMS ? 'active' : null }
            onClick={ () => { toggle(TAB.ITEMS) } }
            role='tab'
          >
            { StringUtils.titleCase(TAB.ITEMS) }
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={ activeTab === TAB.SHIPMENTS ? 'active' : null }
            onClick={ () => { toggle(TAB.SHIPMENTS) } }
            role='tab'
          >
            { StringUtils.titleCase(TAB.SHIPMENTS) }
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={ activeTab === TAB.PAYMENTS ? 'active' : null }
            onClick={ () => { toggle(TAB.PAYMENTS) } }
            role='tab'
          >
            { StringUtils.titleCase(TAB.PAYMENTS) }
          </NavLink>
        </NavItem>
      </Nav>

      <Row>
        <Col md={ 9 }>
          <TabContent
            activeTab={ activeTab }
          >
            <TabPane tabId={ TAB.ITEMS }>
              <ItemPage
                order={ order }
                isLoading={ isLoading }
                handleFetchCustomerOrder={ handleFetchCustomerOrder }
                pageUtils={ pageUtils }
              />
            </TabPane>

            <TabPane tabId={ TAB.SHIPMENTS }>
              <ShipmentPage
                id={ id }
                order={ order }
                pageUtils={ pageUtils }
                setRefetchOrder={ setRefetchOrder }
              />
            </TabPane>

            <TabPane tabId={ TAB.PAYMENTS }>
              <PaymentPage
                order={ order }
                isLoading={ isLoading }
                setOrder={ setOrder }
                setRefetchOrder={ setRefetchOrder }
                pageUtils= { pageUtils }
              />

            </TabPane>
          </TabContent>
        </Col>

        { /* Right Col - Order Details */ }
        <Col md={ 3 }>
          <CustomerOrderDetailCardWithLoader
            isLoading={ isLoading }
            order={ order }
          />

          <Button
            tag={ 'a' }
            className='mb-3'
            color='primary'
            href={ `${process.env.REACT_APP_ZALORA_DASHBOARD_BASE_URL}/order/index/order-detail/filteredStatus/0/id/${order.zalora_order_id}` }
            target={ '_blank' }
            block
            hidden={ !showZaloraOrderButton }
          >
            { 'Lihat Order di Zalora Dashboard' }
          </Button>

          <Button
            className='mb-3'
            color='danger'
            onClick={ handleClickCancel }
            hidden={ order.is_cod || order.status !== STATUS.PENDING }
          >
            { 'Cancel Order' }
          </Button>
          {
            order.sales_admin ? (
              <AdminDetailCard
                admin={ order.sales_admin }
                isLoading={ isLoading }
              />
            ) : (
              isAdminSalesLead && <Alert color='warning'>
                <p>
                  <br />
                  <small>
                    <i className='fas fa-info-circle mr-2' />
                    { 'Order ini belum di assign ke sales admin PSA' }
                    <br/>
                    { 'Klik Button dibawah untuk meng-assign sales admin ke customer order.' }
                    <br/>
                    <br/>
                    { '*Abaikan jika order bukan dibuat oleh PSA' }
                    <br/>
                    { '**Proses Assign hanya bisa dilakukan 1x untuk meminimalisir manipulasi data sales admin yang di-assign' }
                  </small>

                </p>
                <Button
                  className='mb-3'
                  color='primary'
                  onClick={ toggleSalesAdminModal }
                >
                  { 'Assign Sales Admin' }
                </Button>
              </Alert>
            )
          }

          <CustomerDetailCard
            isLoading={ isLoading }
            customer={ order.customer }
          />
          {
            order.shipping_address && (
              <CustomerOrderShippingAddressCard
                isLoading={ isLoading }
                shippingAddress={ order?.shipping_address }
              />
            )
          }
        </Col>

      </Row>

      <GenericModal
        isOpen={ isModalOpen }
        toggle={ toggleSalesAdminModal }
        title={ 'Assign Sales Admin' }
        size='md'
      >
        <SalesAdminForm onSubmitForm={ handleSubmitAssignSalesAdmin } />
      </GenericModal>
    </>
  )
}

CustomerOrderDetailPage.propTypes = propTypes

export default CustomerOrderDetailPage
